import React from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Button } from "@/components/ui/button";
import { Lock } from "lucide-react";
import { CountdownPill } from "./CountdownPill";
import { BidderLimitMeter } from "./BidderLimitMeter";
import { StatusBadge } from "./StatusBadge";
import { VerifiedSellerBadge } from "@/components/VerifiedSellerBadge";
import { money } from "@/lib/api";

export const AuctionCard = ({ a }) => {
    return (
        <Card
            data-testid="auction-card"
            className="group overflow-hidden border-border bg-card shadow-[0_1px_2px_rgba(15,23,42,0.06)] transition-[box-shadow,transform] duration-200 hover:-translate-y-1 hover:shadow-[0_14px_36px_rgba(15,23,42,0.12)]"
        >
            <Link to={`/auctions/${a.id}`} className="block">
                <div className="relative">
                    <AspectRatio ratio={4 / 3}>
                        <img
                            src={a.images?.[0]}
                            alt={a.title}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.04]"
                        />
                    </AspectRatio>
                    <div className="absolute left-3 top-3">
                        <StatusBadge status={a.status} />
                    </div>
                    <div className="absolute right-3 top-3">
                        <CountdownPill endTime={a.end_time} status={a.status} />
                    </div>
                </div>
            </Link>
            <div className="space-y-3 p-4">
                <div className="flex items-start justify-between gap-2">
                    <Link to={`/auctions/${a.id}`}>
                        <h3 data-testid="auction-card-title" className="line-clamp-1 font-display text-base font-semibold tracking-tight">
                            {a.title}
                        </h3>
                    </Link>
                </div>
                <p className="text-xs text-muted-foreground">{a.category_name} · {a.condition}</p>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <span className="truncate">by {a.seller_name}</span>
                    <VerifiedSellerBadge verified={a.seller_verified} compact />
                </div>
                <div className="flex items-center justify-between">
                    <div>
                        <p className="text-xs text-muted-foreground">Starting at</p>
                        <p className="font-display text-lg font-semibold tabular-nums">{money(a.starting_price)}</p>
                    </div>
                    <div className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px] text-muted-foreground">
                        <Lock className="h-3 w-3" aria-hidden /> Sealed bids
                    </div>
                </div>
                <div data-testid="auction-card-bidder-limit">
                    <BidderLimitMeter count={a.unique_bidder_count} max={a.max_bidders} />
                </div>
                <Button asChild data-testid="auction-card-primary-cta" className="w-full rounded-[10px]">
                    <Link to={`/auctions/${a.id}`}>{a.status === "OPEN" ? "View & place sealed bid" : "View result"}</Link>
                </Button>
            </div>
        </Card>
    );
};
